import React from 'react';

import { type Path, useFormContext } from 'react-hook-form';

import { type CreateReservationTypeFormSchemaValues } from 'src/features/reservations/hooks/useCreateReservationType';
import { useDisplayedFormValues } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type PriceFormFieldProps = {
    value: string | number;
    fieldName: Path<CreateReservationTypeFormSchemaValues>;
    label: string;
    disabled?: boolean;
    isRequired?: boolean;
};

const PriceFormField = ({ value, fieldName, label, disabled, isRequired }: PriceFormFieldProps) => {
    const { control } = useFormContext();
    const { setDisplayedFormValues } = useDisplayedFormValues();

    const handleChange = React.useCallback(
        (onChange: (...event: any[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            const isValid = /^\d*(\.?\d{0,2})?$/.test(inputValue);
            if (!isValid && inputValue !== '') return;

            setDisplayedFormValues((prev) => ({ ...prev, [fieldName]: inputValue }));

            const parsed = parseFloat(inputValue);

            if (fieldName === 'tax_percent') {
                if (!isNaN(parsed) && parsed > 100) return;
                const decimal = !isNaN(parsed) ? (parsed / 100).toFixed(4) : '';
                onChange(decimal);
            } else {
                if (!isNaN(parsed)) {
                    onChange(Math.round(parsed * 100));
                } else {
                    onChange(0);
                }
            }
        },
        [],
    );

    return (
        <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog" required={isRequired}>
                        {label}
                    </FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <Input
                                inputMode="decimal"
                                type="text"
                                pattern="^\d*(\.?\d{0,2})?$"
                                value={value ?? '0.00'}
                                placeholder="0.00"
                                disabled={disabled}
                                onChange={handleChange(field.onChange)}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );
};

export default PriceFormField;
